/**
 * Period Templates for Squares Contests
 *
 * Delegates to the shared sports-config package for period data.
 */

import { PERIOD_MAPPING, type PeriodConfig } from '@low6-pickem/sports-config';

// Re-export PeriodConfig as PeriodTemplate for backwards compatibility
export type PeriodTemplate = PeriodConfig;

/**
 * Get default period templates for a sport
 */
export function getPeriodTemplates(sport: string): PeriodTemplate[] {
  return (PERIOD_MAPPING[sport] || []) as PeriodTemplate[];
}

/**
 * Distribute total prize amount evenly across periods
 */
export function distributePrizeEvenly(
  periods: PeriodTemplate[],
  totalPrizeAmount: number
): PeriodTemplate[] {
  if (periods.length === 0 || totalPrizeAmount <= 0) {
    return periods.map(p => ({ ...p, prizeAmount: 0 }));
  }

  const amountPerPeriod = totalPrizeAmount / periods.length;
  // Round to 2 decimal places
  const roundedAmount = Math.round(amountPerPeriod * 100) / 100;

  return periods.map((period, index) => {
    // For the last period, add any remainder to ensure total matches exactly
    if (index === periods.length - 1) {
      const sumSoFar = roundedAmount * (periods.length - 1);
      const lastAmount = totalPrizeAmount - sumSoFar;
      return { ...period, prizeAmount: Math.round(lastAmount * 100) / 100 };
    }
    return { ...period, prizeAmount: roundedAmount };
  });
}

/**
 * Validate that sum of period prizes equals total prize amount
 */
export function validatePeriodPrizes(
  periods: PeriodTemplate[],
  totalPrizeAmount: number
): { valid: boolean; sum: number; difference: number } {
  const sum = periods.reduce((acc, period) => acc + period.prizeAmount, 0);
  const difference = Math.abs(sum - totalPrizeAmount);
  const valid = difference < 0.01; // Allow small floating point differences

  return { valid, sum, difference };
}
